import React, { useState, useRef, useEffect, useMemo } from 'react'
import * as label from '../../shared/util/label'
import {CommissionStatementValidation} from '../../shared/util/constant'
import { Container, Row, Column, Button, Select, Stepper, Icon } from '../../components'
import Grid from '../../components/components/Grid/Grid'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import './ImportCommissionStatement.scss'
import ImportCommissionStatementService from './ImportCommissionStatementService'
import UploadCommissionFileResponse from './model/UploadCommissionFileResponse'
import ConvertCommissionFileResponse from './model/ConvertCommissionFileResponse'
import MigrateCommissionFileResponse from './model/MigrateCommissionFileResponse'

interface Props {
  isSideBarCollapsed: boolean
}

interface ValidationErrors {
  carrierCompany: string
  statementMonth: string
  file: string
} 

const ImportCommissionStatement: React.FunctionComponent<Props> = ({ isSideBarCollapsed }) => {
  const importService = new ImportCommissionStatementService();
  const fileInputRef = useRef<HTMLInputElement>(null)
  const history = useHistory()
  const [currentStep, setCurrentStep] = useState(1)
  const [carrierCompany, setCarrierCompany] = useState('')
  const [statementMonth, setStatementMonth] = useState('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploadResponse, setUploadResponse] = useState<UploadCommissionFileResponse | null>(null)
  const [convertResponse, setConvertResponse] = useState<ConvertCommissionFileResponse | null>(null)
  const [migrateResponse, setMigrateResponse] = useState<MigrateCommissionFileResponse | null>(null) 
  const [errors, setErrors] = useState<ValidationErrors>({
    carrierCompany: '',
    statementMonth: '',
    file: '',
  })


  useEffect(() => {
    const root = document.documentElement
    root.style.setProperty('--marginLeftCustomerRow', isSideBarCollapsed ? '100px' : '250px')
  }, [isSideBarCollapsed])

  const monthOptions = useMemo(() => {
    const month: {value: string; label: string}[] = []
    const now = new Date()
    for(let i = 0; i < 16; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - (i - 3), 1)
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      const yyyy = d.getFullYear()
      month.push( {value: `${mm} - ${yyyy}`, label: `${mm} - ${yyyy}`})
    }
    return month
  }, [])

  const previewColumns = useMemo(() => {
    if (!uploadResponse?.columns) return []
    return uploadResponse.columns.map((colName) => ({
      Header: colName,
      accessor: colName,
      width: 250,
    }))
  }, [uploadResponse?.columns])

  const errorListColumns = useMemo(() => [
    { Header: 'Policy', accessor: 'policy', width: 375 },
    { Header: 'Column', accessor: 'columnName', width: 380 },
    { Header: 'Error Description', accessor: 'errorMessage', width: 680 },
  ], [])


  const handleCarrierChange = (selectedOption: any) => {
    const selectedValue = Array.isArray(selectedOption) ? selectedOption[0] : selectedOption
    setCarrierCompany(selectedValue)
    setErrors(prev => ({ ...prev, carrierCompany: importService.validateCarrier(selectedValue) }))
  }

  const handleMonthChange = (selectedOption: any) => {
    const selectedValue = Array.isArray(selectedOption) ? selectedOption[0] : selectedOption
    setStatementMonth(selectedValue)
    setErrors(prev => ({ ...prev, statementMonth: importService.validateMonth(selectedValue) }))
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null

    if (file) {
      const fileErr = importService.validateFile(file)
      if (fileErr) {
        setErrors(prev => ({ ...prev, file: fileErr }))
        setSelectedFile(null)
      } else {
        setSelectedFile(file)
        setErrors(prev => ({ ...prev, file: '' }))
      }
    } else {
      setSelectedFile(null)
      setErrors(prev => ({ ...prev, file: importService.validateFile(null) }))
    }
  }

  const handleFileUploadAreaClick = () => {
    fileInputRef.current?.click()
  }

  const handleImport = async () => {
    
    const carrierErr = importService.validateCarrier(carrierCompany)
    const monthErr = importService.validateMonth(statementMonth)
    const fileErr = importService.validateFile(selectedFile)
    
    setErrors({
      carrierCompany: carrierErr,
      statementMonth: monthErr,
      file: fileErr
    })

    if (carrierErr || monthErr || fileErr) {
    return; 
    }
    if (!selectedFile) {
      return;
    }
    
    try {
      const response = await importService.uploadCommissionFile(selectedFile, {
        fileName: selectedFile.name,
        carrierName: carrierCompany,
        statementMonth: statementMonth,
      })
      setUploadResponse(response)
      setCurrentStep(2)
    } catch (error: any) {
      const status = error?.response?.status
      const message = status === 400
        ? CommissionStatementValidation.FORMAT_INCORRECT
        : CommissionStatementValidation.IMPORT_FAILED
      toast.error(message, { position: toast.POSITION.TOP_RIGHT })
    }
  }

  const handleCancel = () => {
    setCarrierCompany('')
    setStatementMonth('')
    setSelectedFile(null)
    setUploadResponse(null)
    setErrors({ carrierCompany: '', statementMonth: '', file: '' })
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    history.push('/dashboard')
  }

  const handleConvert = async () => {
    if (!uploadResponse?.fileId) {
      return
    }
    try {
      const response = await importService.processCommissionFile(uploadResponse.fileId, carrierCompany)
      setConvertResponse(response)
      setCurrentStep(3)
    } catch (error) {
      toast.error(CommissionStatementValidation.CONVERT_FAILED, { position: toast.POSITION.TOP_RIGHT })
    }
  }

  const handlePreviewCancel = async () => {
    if(!uploadResponse?.fileId) {
      return
    }

    setCarrierCompany('')
    setStatementMonth('')
    setSelectedFile(null)
    setUploadResponse(null)
    setErrors({ carrierCompany: '', statementMonth: '', file: '' })
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    setCurrentStep(1)
  }

  const handleMigrate = async () => {
    if (!uploadResponse?.fileId) {
      return
    }
    try {
      const response = await importService.migrateCommissionFile(uploadResponse.fileId, carrierCompany)
      setMigrateResponse(response)
      setCurrentStep(4)
    } catch (error) {
      toast.error(CommissionStatementValidation.MIGRATION_FAILED, { position: toast.POSITION.TOP_RIGHT })
    }
  }

  const carrierOptions = [
    { value: 'TowerHill(TH)', label: 'Tower Hill' },
    { value: 'Progressive', label: 'Progressive' },
    { value: 'ASI (Home)', label: 'ASI (Home)' },
    { value: 'AI', label: 'AI American Integrity' },
    { value: 'SafeCo', label: 'Safeco' },
    { value: 'Travelers', label: 'Travelers' },
    { value: 'ASI (Flood)', label: 'ASI (Flood) American Integrity' },
    { value: 'Universal', label: 'Universal'},
    { value: 'Homeowners of America', label: 'Homeowners of America'}
  ]

  const renderUploadBody = () => (
    <div>
      <Row className="rowmargin">
        <Column lg={12} md={12}>
          <Select
            id="carrierCompany"
            isRequired={true}
            label={label.CommissionStatementLabels.CARRIER_COMPANY_LABEL}
            options={carrierOptions}
            onChange={handleCarrierChange}
            className={`mb-3${ errors.carrierCompany ? ' invalid-dropdown custom-dropdown' : ''}`}
            defaultSelected={carrierOptions.filter(({ value }) => value === carrierCompany)}
            multiple={false}
          />
          {errors.carrierCompany && (
            <div className="accounting-field-error">{errors.carrierCompany}</div>
          )}
        </Column>
      </Row>
      <Row className="rowmargin">
        <Column lg={12} md={12}>
          <Select
            id="statementMonth"
            isRequired={true}
            label={label.CommissionStatementLabels.STATEMENT_MONTH_LABEL}
            className={`mb-3${errors.statementMonth ? ' invalid-dropdown custom-dropdown' : ''}`}
            options={monthOptions}
            onChange={handleMonthChange}
            defaultSelected={monthOptions.filter(({ value }) => value === statementMonth)}
            multiple={false}
            sortOptions={false}
          />
          {errors.statementMonth && (
            <div className="accounting-field-error">{errors.statementMonth}</div>
          )}
        </Column>
      </Row>

      <Row className="rowmargin">
        <Column lg={12} md={12}>
          <div
            className={`custom-file-uploader${errors.file ? ' cust-doc-input-error' : ''}`}
            onClick={handleFileUploadAreaClick}
          >
            <input
              ref={fileInputRef}
              id="fileInput"
              type="file"
              accept=".csv, .xlsx"
              className="accounting-file-input-hidden"
              onChange={handleFileSelect}
            />
            <div className="cust-doc-file-icon">
              <Icon icon="file" />
            </div>
            <div>
              <div className="accounting-file-selected-name">
                {selectedFile
                  ? <>
                    <Icon icon="file" className="me-2 accounting-file-icon" />
                    {selectedFile.name}
                  </>
                  : <>
                    {label.CommissionStatementLabels.FILE_UPLOAD_TEXT}
                    <div className="accounting-no-file-message">
                      {label.CommissionStatementLabels.NO_FILE_MESSAGE}
                    </div>
                  </>
                }
              </div>
              {errors.file && (
                <div className="accounting-field-error">{errors.file}</div>
              )}
            </div>
          </div>
        </Column>
      </Row>

      <Row className="rowmargin accounting-btn-row">
        <Column lg={6} md={6} className="text-start">
          <Button
            outlined
            onClick={handleCancel}
            id="btnCancel"
            className="me-2 btn btn-outline-second"
          >
            <Icon icon="cancel" className="me-2" />
            {label.CommissionStatementLabels.CANCEL.toLocaleUpperCase()}
          </Button>
        </Column>
        <Column lg={6} md={6} className="text-end">
          <Button
            id="btnImport"
            onClick={handleImport}
          >
            <Icon icon="import" className="me-2" />
            {label.CommissionStatementLabels.IMPORT.toLocaleUpperCase()}
          </Button>
        </Column>
      </Row>
    </div>
  )

  const renderProcessBody = (uploadResp: UploadCommissionFileResponse, convertResp?: ConvertCommissionFileResponse) => {
    const isErrorState = convertResp != null

    // Swap columns and data dynamically between preview and error state
    const activeColumns = isErrorState ? errorListColumns : previewColumns
    const activeData = convertResp != null ? convertResp.errors : uploadResp.rowPreview
    return (
      <div>
        <div className="accounting-stats-row">
          <div className="accounting-stat-card">
            <div className="accounting-stat-value">{uploadResp.rowCount}</div>
            <div className="accounting-stat-label">{label.CommissionStatementLabels.TOTAL_ROWS}</div>
          </div>
          <div className="accounting-stat-card">
            <div className="accounting-stat-value">{uploadResp.columnCount}</div>
            <div className="accounting-stat-label">{label.CommissionStatementLabels.TOTAL_COLUMNS}</div>
          </div>
          <div className="accounting-stat-card">
            <div className="accounting-stat-value">{uploadResp.nullPolicyCount}</div>
            <div className="accounting-stat-label">{label.CommissionStatementLabels.NULL_POLICY}</div>
          </div>
          {convertResp != null && (
            <div className={convertResp.errorCount > 0 ? 'accounting-stat-card accounting-stat-card-error' : 'accounting-stat-card'}>
              <div className="accounting-stat-value">{convertResp.errorCount > 100 ? '100+' : convertResp.errorCount}</div>
              <div className="accounting-stat-label">{label.CommissionStatementLabels.ERROR_COUNT}</div>
            </div>
          )}
        </div>

        <div className="accounting-preview-table">
          {isErrorState && convertResp?.errorCount === 0 ? (
            <div>
              {label.CommissionStatementLabels.CONVERT_MESSAGE}
            </div>
          ) : (
          <Grid
            id="rowTable"
            data={activeData || []}
            showFilters={false}
            columns={activeColumns}
            hiddenColumns={['selection']}
            paginationDisabled={!isErrorState}
            sortDefault={[]}
          />
          )}
        </div>

        <Row className="rowmargin accounting-btn-row">
          <Column lg={6} md={6} className="text-start">
            <Button outlined onClick={handlePreviewCancel} id="btnBack" className="me-2 btn btn-outline-second">
              <Icon icon="cancel" className="me-2" />
              {label.CommissionStatementLabels.CANCEL.toLocaleUpperCase()}
            </Button>
          </Column>
          <Column lg={6} md={6} className="text-end">
            {!isErrorState ? (
              <Button id="btnConvert" onClick={handleConvert}>
                <Icon icon="arrow-right" className="me-2" />
                {label.CommissionStatementLabels.CONVERT.toLocaleUpperCase()}
              </Button>
            ) : (
              <Button id="btnMigrate" onClick={handleMigrate} disabled={!(convertResp.errorCount === 0)}>
                <Icon icon="arrow-right" className="me-2" />
                {label.CommissionStatementLabels.MIGRATE.toLocaleUpperCase()}
              </Button>
            )}
          </Column>
        </Row>
      </div>
    )
  }
  
  const renderMigrateBody = (uploadResp: UploadCommissionFileResponse, migrateResp: MigrateCommissionFileResponse) => (
    <div>
      <div className="accounting-stats-row">
        <div className="accounting-stat-card">
          <div className="accounting-stat-value">{migrateResp.recordCount}</div>
          <div className="accounting-stat-label">{label.CommissionStatementLabels.MIGRATED_COUNT}</div>
        </div>
        <div className="accounting-stat-card">
          <div className="accounting-stat-value">{uploadResp.rowCount - migrateResp.recordCount}</div>
          <div className="accounting-stat-label">{label.CommissionStatementLabels.SKIPPED_COUNT}</div>
        </div>
      </div>
      <div className={migrateResp.isSuccess ? 'accounting-info-box accounting-migrate-message-success' : 'accounting-info-box accounting-migrate-message-error'}>
        <div>{migrateResp.message}</div>
      </div>
      <Row className="rowmargin accounting-btn-row">
        <Column lg={12} md={12} className="text-end">
          <Button id="btnBack" onClick={handlePreviewCancel} >
            <Icon icon="import" className="me-2" />
              {label.CommissionStatementLabels.REIMPORT.toLocaleUpperCase()}
          </Button>
        </Column>
      </Row>
    </div>
  )

  const stepTitles: Record<number, string> = {
    1: label.CommissionStatementLabels.STEP1_TITLE,
    2: label.CommissionStatementLabels.STEP2_TITLE,
    3: label.CommissionStatementLabels.STEP3_TITLE,
    4: label.CommissionStatementLabels.STEP4_TITLE,
  }

  const stepInfoBoxes: Record<number, string> = {
    1: label.CommissionStatementLabels.INFO_BOX1,
    2: label.CommissionStatementLabels.INFO_BOX2,
    3: label.CommissionStatementLabels.INFO_BOX3,
    4: label.CommissionStatementLabels.INFO_BOX4,
  }

  const hasConvertErrors = currentStep === 3 && (convertResponse?.errorCount ?? 0) > 0
  const activeInfoBox = hasConvertErrors
    ? label.CommissionStatementLabels.INFO_BOX3_ERROR
    : currentStep === 4 && migrateResponse != null && !migrateResponse.isSuccess
    ? label.CommissionStatementLabels.INFO_BOX4_ERROR
    : stepInfoBoxes[currentStep]

  return (
    <Container fluid className="accounting-container">
      <div className="accounting-header">
        {label.CommissionStatementLabels.PAGE_TITLE}
      </div>

      <Stepper
        className="mb-4"
        currentStep={currentStep}
        steps={[
          { id: 1, label: label.CommissionStatementLabels.STEP_UPLOAD },
          { id: 2, label: label.CommissionStatementLabels.STEP_PREVIEW },
          { id: 3, label: label.CommissionStatementLabels.STEP_CONVERT },
          { id: 4, label: label.CommissionStatementLabels.STEP_MIGRATE },
        ]}
      />

      <div className="accounting-card">
        <h2>{stepTitles[currentStep]}</h2>
        <div className={hasConvertErrors ? 'accounting-info-box accounting-info-box-error' : 'accounting-info-box'}>{activeInfoBox}</div>
        {currentStep === 1 && renderUploadBody()}
        {currentStep === 2 && uploadResponse && renderProcessBody(uploadResponse)}
        {currentStep === 3 && uploadResponse && renderProcessBody(uploadResponse, convertResponse ?? undefined)}
        {currentStep === 4 && uploadResponse && migrateResponse && renderMigrateBody(uploadResponse, migrateResponse)}
      </div>
    </Container>
  )
}

export default ImportCommissionStatement